import I18n from 'I18n';
import always from 'transmute/always';
import { List, Map as ImmutableMap } from 'immutable';
import { DSFilterFromObjectSeg, DSFilterToObjectSeg } from '../../converters/Converters';
import { InboundDbListSeg } from 'customer-data-filters-ui/filterQueryFormat/strategy/StrategyTypes';
import { deepFreeze } from '../../utils/utils';
import { StrategyBase } from '../StrategyBase';
import { getCRMSearchFieldDefinitionsForFilterFamily } from '../../converters/crmSearch/fieldDefinitions/CRMSearchFieldDefinitions';
import { getReportingOperatorsForType } from '../../converters/reporting/ReportingILSPropertyTypeToOperators';
import { unwrapSingleILSFilter, wrapSingleILSFilter } from '../InboundDbListSegStrategyUtils';
import * as ExternalOptionTypes from 'customer-data-objects/property/ExternalOptionTypes';
import { BLOG_AUTHOR, BLOG_TAG, BLOGS, ANALYTICS_VIEW, SUBDOMAIN, SUBDOMAIN_ID, INBOUND_DB_IMPORT, INBOUND_DB_LISTS
// @ts-expect-error untyped dependency
} from 'reference-resolvers/constants/ReferenceObjectTypes';
import { getMeOption, getMyTeamOption, getMyUserIdOption } from '../../utils/specialFieldUtils';
import { getAllClosedWonOption } from 'customer-data-objects/deal/DealStageIdOptions';
import { And } from '../../filterQueryFormat/logic/FilterLogicGroup';
import { fromObjectSegFilterStrict } from '../../converters/objectSeg/fromObjectSegFilter';
import * as Operators from '../../filterQueryFormat/operator/Operators';
import RollingDateConfig, { validateRollingDateConfig } from '../../filterQueryFormat/rollingDates/RollingDateConfig';
const getSpecialFieldOptions = () => ({
  OWNER: () => List.of(getMeOption()),
  DEAL_STAGE: () => List.of(getAllClosedWonOption()),
  TEAM: () => List.of(getMyTeamOption()),
  USER: () => List.of(getMyUserIdOption())
});
const getFilterFamilyGroupHeading = () => undefined;
const getFilterFamilyOptions = getFilterFamilyName => props => {
  const filterFamilyName = getFilterFamilyName(props.baseFilterFamily, props, false);
  const options = [{
    text: I18n.text('newCustomerDataFilters.FilterFamilyGroup.currentObject'),
    options: [{
      text: filterFamilyName,
      value: props.baseFilterFamily
    }]
  }];
  return options;
};
export const serializer = {
  fromFormat: DSFilterFromObjectSeg,
  toFormat: DSFilterToObjectSeg
};

/**
 * @temporary supportsLegacyILSDateTimeFilters is used to help migrate reporting off the legacy components.  This should be removed once the migration is complete.
 * @expiration 2025-09-30
 * @deprecated
 */
export const legacyILSDateTimeFilterSerializer = {
  fromFormat: (value, baseFilterFamily, getField, isUngated, getOperatorsForType, serializerOptions) => {
    serializerOptions = Object.assign({}, serializerOptions, {
      supportsLegacyILSDateTimeFilters: true
    });
    return DSFilterFromObjectSeg(value, baseFilterFamily, getField, isUngated, getOperatorsForType, serializerOptions);
  },
  toFormat: (value, isUngated, serializerOptions) => {
    serializerOptions = Object.assign({}, serializerOptions, {
      supportsLegacyILSDateTimeFilters: true
    });
    return DSFilterToObjectSeg(value, isUngated, serializerOptions);
  }
};
export class ReportingILSStrategy extends StrategyBase {}
const getReferenceResolvers = resolvers => {
  return ImmutableMap({
    [ExternalOptionTypes.INBOUND_DB_IMPORT]: resolvers.get(INBOUND_DB_IMPORT),
    [ExternalOptionTypes.INBOUND_DB_LIST]: resolvers.get(INBOUND_DB_LISTS),
    [ANALYTICS_VIEW]: resolvers.get(ANALYTICS_VIEW),
    [BLOG_AUTHOR]: resolvers.get(BLOG_AUTHOR),
    [BLOG_TAG]: resolvers.get(BLOG_TAG),
    [BLOGS]: resolvers.get(BLOGS),
    [SUBDOMAIN]: resolvers.get(SUBDOMAIN),
    [SUBDOMAIN_ID]: resolvers.get(SUBDOMAIN_ID)
  });
};
export const ReportingILSStrategyBuilder = ({
  getField,
  getFields,
  getFilterFamilyName,
  getFilterFamilyObjectName,
  reportingFilterContext,
  isIncludeObjectsWithNoValueSetCheckboxEnabled = false,
  supportsLegacyILSDateTimeFilters = true,
  isRelativeTimeProperty = false
}) => {
  const strategy = new ReportingILSStrategy({
    getField,
    getFieldDefinitions: getCRMSearchFieldDefinitionsForFilterFamily,
    getFields,
    getFilterFamilyGroupHeading,
    getFilterFamilyName,
    getFilterFamilyObjectName,
    getFilterFamilyOptions: getFilterFamilyOptions(getFilterFamilyName),
    getIsFiscalYearEnabled: always(true),
    getOperatorsForType: getReportingOperatorsForType(reportingFilterContext, supportsLegacyILSDateTimeFilters, {
      isRelativeTimeProperty
    }),
    getReferenceResolvers,
    getSpecialFieldOptions,
    serializer: supportsLegacyILSDateTimeFilters ? legacyILSDateTimeFilterSerializer : serializer,
    wrapSingleFilter: wrapSingleILSFilter,
    unwrapSingleFilter: unwrapSingleILSFilter,
    strategyType: InboundDbListSeg,
    isIncludeObjectsWithNoValueSetCheckboxEnabled
  });
  return deepFreeze(strategy);
};

/**
 * @temporary export placeholder functions is used to help migrate reporting off the legacy components.  This should be removed once the migration is complete.
 * @expiration 2025-07-30
 * @deprecated
 */
ReportingILSStrategyBuilder.fromObjectSegFilterStrict = fromObjectSegFilterStrict;
ReportingILSStrategyBuilder.convertFromDashboardFilters = (value, isUngated) => {
  const logicGroup = And.of(value);
  return DSFilterToObjectSeg(logicGroup, isUngated, {
    supportsLegacyILSDateTimeFilters: true
  });
};
ReportingILSStrategyBuilder.Operators = Operators;
ReportingILSStrategyBuilder.RollingDateConfig = RollingDateConfig;
ReportingILSStrategyBuilder.And = And;
ReportingILSStrategyBuilder.getReportingOperatorsForType = getReportingOperatorsForType;
ReportingILSStrategyBuilder.validateRollingDateConfig = validateRollingDateConfig;